/**
 * A link from an internal screen to a PUBLIC address, written once.
 *
 * The prototype is a single hash-routed file, so every link it makes to a public page is
 * `#/c/{code}`. The product is not: `/c/:code` and `/claim/:token` are real paths, and an `href`
 * of `#/c/…` there appends a fragment to whatever page the operator is standing on — a link that
 * looks right, is offered on every credential, and goes nowhere. §11 calls that the decorative
 * button, and it is worse here because it looks like it worked.
 *
 * `useHref` asks the router the component is actually under, so the same JSX renders `#/c/…` in
 * the demo's `HashRouter` — byte-identical to what the parity capture has always measured — and
 * `/c/…` in the product's `BrowserRouter`.
 *
 * It stays a plain `<a>` rather than a `<Link>`: these open in a new tab, and a client-side
 * navigation is not what «open the public page» means.
 */
import type { AnchorHTMLAttributes, ReactElement } from 'react';
import { useHref } from 'react-router-dom';

export interface PublicLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** A public path, always with a leading slash: `/c/HPW-…`, `/claim/…`, `/verify`. */
  to: string;
}

export function PublicLink({ to, ...rest }: PublicLinkProps): ReactElement {
  return <a href={useHref(to)} {...rest} />;
}
